/**
 * Locale hook.
 *
 * Ownership is split deliberately: the **UI store** holds the client preference (what the user last picked on this
 * device) and the **account profile** holds the durable preference. When a user is signed in, a change is written
 * to both, so a fresh browser picks up the account setting while an anonymous visitor keeps a local choice.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { setLocale } from '@/lib/i18n';
import { formatJalaliDate } from '@/lib/format/jalali';
import { formatNumber } from '@/lib/format/numbers';
import type { AppLocale } from '@/stores/uiStore';
import { defaultDigitPreference, useUiStore } from '@/stores/uiStore';

/** Value returned by {@link useLocale}. */
export interface UseLocaleResult {
  /** Active interface locale. */
  locale: AppLocale;
  /** Whether the interface renders right to left. */
  direction: 'rtl' | 'ltr';
  /** Switches the interface language and updates the document direction. */
  changeLocale: (next: AppLocale) => Promise<void>;
  /** Flips between Persian and English. */
  toggleLocale: () => Promise<void>;
}

/** Reads and changes the active locale. */
export function useLocale(): UseLocaleResult {
  const { i18n } = useTranslation();
  const locale = useUiStore((state) => state.locale);
  const storeLocale = useUiStore((state) => state.setLocale);

  const changeLocale = useCallback(
    async (next: AppLocale) => {
      await setLocale(next);
      storeLocale(next);
    },
    [storeLocale],
  );

  const toggleLocale = useCallback(
    () => changeLocale(locale === 'fa' ? 'en' : 'fa'),
    [changeLocale, locale],
  );

  return {
    // `i18n.language` is the source of truth once bootstrapped, but the store value is what renders first.
    locale:
      i18n.language === 'en' || i18n.language === 'fa' ? (i18n.language as AppLocale) : locale,
    direction: locale === 'fa' ? 'rtl' : 'ltr',
    changeLocale,
    toggleLocale,
  };
}

/**
 * Formats a date and a quantity with the active locale's conventions.
 *
 * Provided so pages never call the formatters with hard-coded options — the digit preference in particular must
 * follow the locale, or a Persian page would show Latin numerals in half of its widgets.
 */
export function useFormatters() {
  const locale = useUiStore((state) => state.locale);
  const digits = defaultDigitPreference(locale);

  return {
    locale,
    digits,
    number: (value: number) => formatNumber(value, { locale, digits }),
    date: (value: string | Date | null | undefined) => formatJalaliDate(value, { locale, digits }),
  };
}
